import React, { useState, useRef } from 'react';
import './DocumentUpload.css';
import { storage } from '../../../firebase.config';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
const DocumentUpload = () => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileInputRef = useRef(null);

  // Get backend URL for API calls - using the same logic as other working tabs
  const getBackendUrl = () => {
    // First, try React environment variables
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }

    // Second, try regular environment variables
    if (process.env.BACKEND_URL) {
      return process.env.BACKEND_URL;
    }

    // Third, check if we're running on Firebase Hosting and use Cloud Run backend
    if (typeof window !== 'undefined' && window.location.hostname) {
      const hostname = window.location.hostname;

      // Check for Firebase domains (including the current working one)
      if (hostname.includes('firebaseapp.com') ||
        hostname.includes('web.app') ||
        hostname.includes('viraltrade.in') ||
        hostname.includes('viraltrade-19032')) {
        console.log('🔍 DocumentUpload: Detected Firebase domain, using Cloud Run backend');
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }

      // Fourth, check if we're running on Cloud Run and use hardcoded URL
      if (hostname.includes('run.app')) {
        console.log('🔍 DocumentUpload: Detected Cloud Run domain, using Cloud Run backend');
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
    }

    // Finally, fallback to localhost for development
    console.log('🔍 DocumentUpload: Using localhost fallback for development');
    return 'http://localhost:8000';
  };

  const BASE_URL = getBackendUrl();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = async (files) => {
    setUploading(true);

    try {
      // Simulate file upload - replace with actual API call
      const uploadPromises = Array.from(files).map(async (file) => {
        // Simulate upload delay
        await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

        return {
          id: Date.now() + Math.random(),
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date(),
          status: 'success',
          file: file // Store the actual file object for later upload
        };
      });

      const uploadedFileData = await Promise.all(uploadPromises);
      setUploadedFiles(prev => [...prev, ...uploadedFileData]);
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const onButtonClick = () => {
    fileInputRef.current.click();
  };

  // PDF upload function using presigned URL
  // const uploadPdf = async (file) => {
  //   try {
  //     setUploading(true);
  //     setUploadStatus('Generating upload URL...');

  //     // Step 1: get local presigned URL
  //     const genRes = await fetch(`${BASE_URL}/api/generate-local-upload-url`, {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ fileName: file.name })
  //     });

  //     if (!genRes.ok) {
  //       throw new Error('Failed to generate upload URL');
  //     }

  //     const { uploadUrl } = await genRes.json();
  //     setUploadStatus('Uploading file...');

  //     // Step 2: upload the file using FormData (field name must be 'file')
  //     const formData = new FormData();
  //     formData.append('file', file);

  //     const upRes = await fetch(`${BASE_URL}${uploadUrl}`, {
  //       method: 'POST',
  //       body: formData
  //     });

  //     if (!upRes.ok) {
  //       throw new Error('Failed to upload file');
  //     }

  //     const result = await upRes.json();
  //     setUploadStatus('Upload successful!');

  //     // Update the file status to uploaded
  //     setUploadedFiles(prev => prev.map(f =>
  //       f.id === file.id ? { ...f, status: 'uploaded', uploadResult: result } : f
  //     ));

  //     return result;
  //   } catch (error) {
  //     console.error('Upload failed:', error);
  //     setUploadStatus(`Upload failed: ${error.message}`);

  //     // Update the file status to failed
  //     setUploadedFiles(prev => prev.map(f =>
  //       f.id === file.id ? { ...f, status: 'failed', error: error.message } : f
  //     ));

  //     throw error;
  //   } finally {
  //     setUploading(false);
  //     // Clear status message after 3 seconds
  //     setTimeout(() => setUploadStatus(''), 3000);
  //   }
  // };
  const uploadPdf = async (file) => {
    try {
      setUploading(true);
      setUploadStatus('Generating upload URL...');

      // Step 1: Get presigned upload URL from backend
      const genRes = await fetch(`${BASE_URL}/api/generate-local-upload-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name })
      });

      if (!genRes.ok) {
        const errorData = await genRes.text();
        throw new Error(`Failed to generate upload URL: ${errorData}`);
      }

      const { uploadUrl } = await genRes.json();
      setUploadStatus('Uploading file to backend...');

      // Step 2: Upload file to backend using the presigned URL
      const formData = new FormData();
      formData.append('file', file);

      const upRes = await fetch(`${BASE_URL}${uploadUrl}`, {
        method: 'POST',
        body: formData
      });

      if (!upRes.ok) {
        const errorData = await upRes.text();
        throw new Error(`Backend upload failed: ${errorData}`);
      }

      const result = await upRes.json();
      setUploadStatus('Upload successful! Processing with Firebase...');

      try {
        // Step 3: Upload to Firebase Storage
        const storageRef = ref(storage, `reports/${file.name}`);
        console.log('Starting Firebase upload...', { file, storageRef });

        // Upload the file
        const snapshot = await uploadBytes(storageRef, file);
        console.log('Uploaded a blob or file!', snapshot);

        // Get the download URL
        const firebaseUrl = await getDownloadURL(snapshot.ref);
        console.log('File available at', firebaseUrl);

        // Update file status in UI
        setUploadedFiles(prev => prev.map(f =>
          f.id === file.id ? { ...f, status: 'uploaded', uploadResult: result, firebaseUrl } : f
        ));

        return firebaseUrl;
      } catch (firebaseError) {
        console.error('Firebase upload failed:', firebaseError);
        throw new Error(`Firebase upload failed: ${firebaseError.message}`);
      }
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadStatus(`Upload failed: ${error.message}`);
      setUploadedFiles(prev => prev.map(f =>
        f.id === file.id ? { ...f, status: 'failed', error: error.message } : f
      ));
      throw error;
    }
    // finally {
    //   setUploading(false);
    //   setTimeout(() => setUploadStatus(''), 3000);
    // }
  };
  // Handle upload button click - upload the first item in uploadedFiles array
  const handleUploadButtonClick = async (e) => {
    e.preventDefault()
    if (uploadedFiles.length === 0) {
      setUploadStatus('No files to upload');
      return;
    }

    const firstFile = uploadedFiles[0];

    // Check if it's a PDF file
    if (firstFile.type !== 'application/pdf' && !firstFile.name.toLowerCase().endsWith('.pdf')) {
      setUploadStatus('First file must be a PDF');
      return;
    }

    try {
      const res = await uploadPdf(firstFile.file);
      console.log("RESULT:::::", res)
      if (res.document_id) {
        console.log("Stored document ID:", res.document_id);
        // You can now use this ID to reference the document
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const getFileStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '📄';
      case 'uploaded':
        return '✅';
      case 'failed':
        return '❌';
      default:
        return '📄';
    }
  };

  return (
    <div className="document-upload">
      <div
        className={`upload-area ${dragActive ? 'drag-active' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.xls"
          onChange={handleChange}
          className="file-input"
        />

        <div className="upload-content">
          <div className="upload-icon">📄</div>
          <h3>Upload Documents</h3>
          <p>Drag and drop files here, or click to browse</p>
          <p className="file-types">Supported: PDF, DOC, DOCX, TXT, CSV, XLSX</p>
          <button
            type="button"
            onClick={onButtonClick}
            className="browse-button"
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Browse Files'}
          </button>
        </div>
      </div>

      {uploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
          <p>{uploadStatus || 'Processing files...'}</p>
        </div>
      )}

      {uploadStatus && !uploading && (
        <div className={`upload-status ${uploadStatus.includes('failed') ? 'error' : 'success'}`}>
          {uploadStatus}
        </div>
      )}

      {uploadedFiles.length > 0 && (
        <div className="uploaded-files">
          <h4>Recently Uploaded</h4>
          <div className="file-list">
            {uploadedFiles.map((file) => (
              <div key={file.id} className={`file-item ${file.status}`}>
                <div className="file-info">
                  <span className="file-icon">{getFileStatusIcon(file.status)}</span>
                  <div className="file-details">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                    {file.status === 'failed' && file.error && (
                      <span className="file-error">{file.error}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="remove-file"
                  title="Remove file"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              className="browse-button upload-btn"
              onClick={handleUploadButtonClick}
              disabled={uploading || uploadedFiles.length === 0}
            >
              {uploading ? 'Uploading...' : 'Upload First PDF'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload; 